import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import api from "@/lib/api";
import Seo from "@/components/Seo";
import { absoluteAssetUrl, CANONICAL_ORIGIN } from "@/lib/seo";
import { PageSkeleton } from "@/components/LoadingSkeleton";
import { getAnalyticsSessionId, trackEvent } from "@/lib/analytics";

function ArticleContent({ content }) {
  const blocks = String(content || "").split(/\n{2,}/).map((block) => block.trim()).filter(Boolean);
  return <div className="article-copy">{blocks.map((block, index) => {
    if (block.startsWith("### ")) return <h3 key={index}>{block.slice(4)}</h3>;
    if (block.startsWith("## ")) return <h2 key={index}>{block.slice(3)}</h2>;
    if (block.startsWith("# ")) return <h2 key={index}>{block.slice(2)}</h2>;
    const lines = block.split("\n");
    if (lines.every((line) => line.startsWith("- "))) return <ul key={index}>{lines.map((line) => <li key={line}>{line.slice(2)}</li>)}</ul>;
    return <p key={index}>{lines.map((line, lineIndex) => <span key={lineIndex}>{line}{lineIndex < lines.length - 1 && <br/>}</span>)}</p>;
  })}</div>;
}

export default function BlogArticle() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [missing, setMissing] = useState(false);
  useEffect(() => {
    setArticle(null); setMissing(false);
    api.get(`/blog/${slug}`).then((r) => {
      setArticle(r.data);
      trackEvent("blog_article_viewed", { category: r.data.category_id, metadata: { slug } }, `blog-view:${getAnalyticsSessionId()}:${slug}`);
    }).catch(() => setMissing(true));
  }, [slug]);
  if (missing) return <main className="mx-auto max-w-3xl px-4 py-20 text-center"><Seo title="Artikel ikke fundet | SpørgMig" description="Artiklen findes ikke eller er ikke publiceret." path={`/blog/${slug}`} noIndex/><h1 className="text-2xl font-semibold">Artiklen blev ikke fundet</h1><Link to="/blog" className="mt-4 inline-block text-brand-fresh underline">Se alle guides</Link></main>;
  if (!article) return <PageSkeleton cards={3}/>;
  const canonical = `${CANONICAL_ORIGIN}/blog/${article.slug}`;
  const schemas = [
    {
      "@context": "https://schema.org", "@type": "Article", headline: article.title,
      description: article.meta_description || article.excerpt, image: article.cover_image_url ? [absoluteAssetUrl(article.cover_image_url)] : undefined,
      datePublished: article.published_at, dateModified: article.updated_at || article.published_at,
      author: { "@type": "Organization", name: article.author_name || "SpørgMig" },
      publisher: { "@type": "Organization", name: "SpørgMig", url: CANONICAL_ORIGIN }, mainEntityOfPage: canonical,
    },
    {
      "@context": "https://schema.org", "@type": "BreadcrumbList", itemListElement: [
        { "@type": "ListItem", position: 1, name: "Forside", item: `${CANONICAL_ORIGIN}/` },
        { "@type": "ListItem", position: 2, name: "Viden", item: `${CANONICAL_ORIGIN}/blog` },
        { "@type": "ListItem", position: 3, name: article.title, item: canonical },
      ],
    },
  ];
  const cta = article.category ? `/beskriv-problem?category=${article.category.id}` : "/beskriv-problem";
  return <>
    <Seo title={article.seo_title || `${article.title} | SpørgMig`} description={article.meta_description || article.excerpt} path={`/blog/${article.slug}`} image={article.cover_image_url} type="article" schemas={schemas}/>
    <main className="mx-auto max-w-5xl px-4 py-8 sm:px-8 sm:py-12" data-testid="blog-article">
      <nav className="text-xs text-brand-muted"><Link to="/blog" className="hover:text-brand-fresh">Viden</Link>{article.category && <> · <Link to={`/kategorier/${article.category.slug}`} className="hover:text-brand-fresh">{article.category.name}</Link></>}</nav>
      <article className="mx-auto mt-5 max-w-[720px]">
        <h1 className="text-3xl font-bold leading-tight tracking-tight text-brand-ink sm:text-5xl">{article.title}</h1>
        <p className="mt-4 text-lg leading-8 text-brand-body">{article.excerpt}</p>
        <div className="mt-4 text-xs text-brand-muted">{new Date(article.published_at).toLocaleDateString("da-DK", { day: "numeric", month: "long", year: "numeric" })} · {article.author_name || "SpørgMig"}</div>
        {article.cover_image_url && <img src={absoluteAssetUrl(article.cover_image_url)} alt={`Forsidebillede til ${article.title}`} className="mt-8 aspect-[16/9] w-full rounded-2xl object-cover"/>}
        <ArticleContent content={article.content}/>
        {(article.category || article.related_experts?.length > 0) && <section className="mt-10 border-t border-brand-line pt-7">
          <h2 className="text-xl font-semibold text-brand-ink">Relevant hjælp</h2>
          {article.category && <Link to={`/kategorier/${article.category.slug}`} className="mt-3 inline-flex text-sm font-medium text-brand-fresh">Se eksperter i {article.category.name}</Link>}
          <div className="mt-4 grid gap-3 sm:grid-cols-2">{(article.related_experts || []).map((expert) => <Link key={expert.id} to={`/eksperter/${expert.id}`} className="card-outline flex items-center gap-3 p-3 hover:border-brand-fresh"><img src={absoluteAssetUrl(expert.photo_url)} alt={`Profilbillede af ${expert.name}`} loading="lazy" className="h-11 w-11 rounded-full object-cover"/><span className="min-w-0"><span className="block font-semibold text-brand-ink">{expert.name}</span><span className="block truncate text-xs text-brand-muted">{expert.headline}</span></span></Link>)}</div>
        </section>}
        <section className="mt-10 rounded-2xl bg-brand-soft p-6 sm:p-8">
          <h2 className="text-xl font-semibold text-brand-ink">Sidder du stadig fast?</h2>
          <p className="mt-2 text-sm leading-6 text-brand-body">Beskriv dit konkrete problem og få tilbud fra relevante eksperter.</p>
          <Link to={cta} onClick={() => trackEvent("blog_cta_clicked", { category: article.category_id, metadata: { slug: article.slug } })} className="btn-primary mt-4 inline-flex items-center gap-2">Spørg en ekspert <ArrowRight size={16}/></Link>
        </section>
      </article>
    </main>
  </>;
}

